import { useState, useRef } from 'react'
import { Button, Spinner } from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'

const MAX_FILE_SIZE = 8 * 1024 * 1024 // 8MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

interface MediaUploadProps {
  mediaUrls: string[]
  onMediaChange: (urls: string[]) => void
  required?: boolean
  maxFiles?: number
  onError?: (message: string) => void
}

export function MediaUpload({
  mediaUrls,
  onMediaChange,
  required = false,
  maxFiles = 1,
  onError,
}: MediaUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const remaining = maxFiles - mediaUrls.length
    if (remaining <= 0) {
      onError?.(`Maximum ${maxFiles} image${maxFiles > 1 ? 's' : ''} allowed.`)
      return
    }

    const filesToUpload = Array.from(files).slice(0, remaining)

    for (const file of filesToUpload) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        onError?.('Unsupported file type. Use JPEG, PNG, or WebP.')
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        onError?.('File is too large. Maximum size is 8MB.')
        continue
      }

      setIsUploading(true)
      try {
        const response = await api.uploadMedia(file)
        onMediaChange([...mediaUrls, response.url])
      } catch (err) {
        const message = err instanceof ApiError
          ? getErrorMessage(err, 'Failed to upload media')
          : 'Failed to upload media'
        onError?.(message)
      } finally {
        setIsUploading(false)
      }
    }

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemove = (index: number) => {
    onMediaChange(mediaUrls.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="block text-sm font-medium">
          Images
          {required && <span className="text-error ml-1">*</span>}
        </label>
        {required && mediaUrls.length === 0 && (
          <span className="text-xs text-error">Image required</span>
        )}
      </div>

      {/* Thumbnail previews */}
      {mediaUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {mediaUrls.map((url, index) => (
            <div key={url} className="relative group">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-20 h-20 object-cover rounded border border-border"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-error text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {mediaUrls.length < maxFiles && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            multiple={maxFiles > 1}
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" /> Uploading...
              </span>
            ) : (
              `Add Image${maxFiles > 1 ? 's' : ''}`
            )}
          </Button>
          <p className="text-xs text-secondary-text mt-1">
            JPEG, PNG, or WebP. Max 8MB.
          </p>
        </div>
      )}
    </div>
  )
}
